'use es6';
/*
 * Selectors for *Active* layout being edited.
 */

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import Categories from 'ContentUtils/constants/Categories';
import { byName as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import { SYSTEM, EMAIL, BLOG, PAGE } from 'DesignData/enums/DesignAssetCategories';
import { HUBL_CSS, CSS as TEMPLATE_FILE_TYPE_CSS } from 'DesignData/constants/TemplateFileTypes';
import { getTemplateId } from 'DesignData/selectors/app';
import { hasEmailAccess as getHasEmailAccess } from 'DesignData/selectors/auth';
import { getIsInLockedFolderFactory } from 'DesignData/selectors/finder';
import { getLayoutTemplateType } from 'DesignData/selectors/layouts';

function getDesignAssetCategory(categoryId) {
  switch (categoryId) {
    case Categories.unmapped.id:
      return SYSTEM;

    case Categories.email.id:
      return EMAIL;

    case Categories.blog_post.id:
      return BLOG;

    case Categories.landing_page.id:
    case Categories.site_page.id:
    case Categories.legacy_page.id:
      return PAGE;

    default:
      return -1;
  }
} // TODO: Remove this and use `getTemplateBuffer()` instead.


export const getById = state => state.templateBuffers.byId;

const getCloneErrorById = state => state.templateBuffers.cloneErrorById;

const getIsPublishingById = state => state.templateBuffers.isPublishingById;

const getIsSavingById = state => state.templateBuffers.isSavingById;

const getMoveRenameErrorById = state => state.templateBuffers.moveRenameErrorById;

const getPublishErrorById = state => state.templateBuffers.publishErrorById;

const getSaveErrorById = state => state.templateBuffers.saveErrorById;

const getFetchErrorById = state => state.templateBuffers.fetchErrorById;
/**
 * Required Props:
 *  templateId || assetId
 * Optional Props:
 *  revisionId
 */


export const getTemplateBuffer = (state, props) => {
  const {
    templateId,
    assetId,
    revisionId
  } = props;
  const id = templateId || assetId;
  return revisionId ? SeamlessImmutable.getIn(state, ['templateRevisions', 'templates', id, 'revisions', revisionId]) : SeamlessImmutable.getIn(state, ['templateBuffers', 'byId', id]);
};
/**
 * Required Props:
 *  templateId || assetId
 * Optional Props:
 *  revisionId
 */

export const getTemplateType = (state, props) => {
  const buffer = getTemplateBuffer(state, props);
  if (!buffer) return TemplateTypes.unmapped.id;
  return buffer.templateType;
};
export const getTemplateLabel = (state, props) => {
  const template = getTemplateBuffer(state, props);
  return template && template.label || null;
};
export const getRequestStatus = createSelector([getTemplateId, getCloneErrorById, getIsPublishingById, getIsSavingById, getMoveRenameErrorById, getPublishErrorById, getSaveErrorById, getFetchErrorById], (templateId, cloneErrorById, isPublishingById, isSavingById, moveRenameErrorById, publishErrorById, saveErrorById, fetchErrorById) => {
  return {
    cloneError: cloneErrorById[templateId],
    isPublishing: isPublishingById[templateId],
    isSaving: isSavingById[templateId],
    moveRenameError: moveRenameErrorById[templateId],
    publishError: publishErrorById[templateId],
    saveError: saveErrorById[templateId],
    fetchError: fetchErrorById[templateId]
  };
});
/**
 * @param props
 * props {
 *  templateId || assetId
 * }
 */

export const makeGetIsReadOnly = () => createSelector([getTemplateBuffer, getIsInLockedFolderFactory, getHasEmailAccess], (templateBuffer, getIsInLockedFolder, hasEmailAccess) => {
  if (!templateBuffer) return true;
  const {
    categoryId,
    generatedFromLayoutId,
    isBuiltin,
    isReadOnly
  } = templateBuffer;
  const isEmail = categoryId === Categories.email.id;
  const isLocked = getIsInLockedFolder(templateBuffer.folderId);
  return isBuiltin || isReadOnly || !!generatedFromLayoutId || isEmail && !hasEmailAccess || isLocked;
});
/**
 * Get meta properties of a template.
 *
 * Required Props:
 *  templateId || assetId
 * Optional Props:
 *  revisionId
 */

export const makeGetMeta = () => {
  const getIsReadOnly = makeGetIsReadOnly();
  let state = SeamlessImmutable({
    designAssetCategory: null,
    isAvailableForNewContent: false,
    isCss: false,
    isBlog: false,
    isEmail: false,
    isPage: false,
    isReadOnly: false
  });
  return createSelector([getTemplateBuffer, getIsReadOnly], (templateBuffer, isReadOnly) => {
    if (!templateBuffer) {
      return state;
    }

    const {
      categoryId,
      templateType,
      isAvailableForNewContent,
      meta
    } = templateBuffer;
    const templateFileType = templateBuffer.getFileType();
    const isEmail = categoryId === Categories.email.id;
    state = state.merge(Object.assign({}, meta, {
      designAssetCategory: getDesignAssetCategory(categoryId),
      isAvailableForNewContent,
      isCss: templateType === TemplateTypes.css.id || templateFileType === HUBL_CSS || templateFileType === TEMPLATE_FILE_TYPE_CSS,
      isBlog: categoryId === Categories.blog_post.id,
      isEmail,
      isPage: categoryId === Categories.landing_page.id,
      isReadOnly
    }));
    return state;
  });
};
/**
 * @return {(Number|String|Null)}
 */

export const makeGetTemplateType = () => {
  return createSelector([getLayoutTemplateType, getTemplateType], (layoutTemplateType, templateType) => {
    return layoutTemplateType || templateType;
  });
};